import "./Menu.css";
import Navbar from "../../components/navbar/Navbar";
import MenuCategory from "../../components/menu/MenuCategory";
import MenuPdfButton from "../../components/menu/MenuPdfButton";
import { menuData } from "../../components/menu/menuData";
import { getRemoteMenu } from "../../lib/menuService";
import Footer from "../../components/footer/Footer";
import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import Seo from "../../components/Seo";

const categoryLabels = {
    antipasti: "Antipasti e Piatti unici",
    crostoni: "Crostoni",
    cucina: "Cucina",
    pizzeria: "Pizze",
};

const extraFilterButtons = [
    { id: "vegetariana", label: "Vegetariane" },
    { id: "piccante", label: "Piccanti" },
    { id: "pesce", label: "Pesce" },
    { id: "carne", label: "Carne" },
];

export default function Menu() {
    const [currentMenu, setCurrentMenu] = useState(menuData);
    const [activeCategory, setActiveCategory] = useState("all");
    const [activeExtraFilters, setActiveExtraFilters] = useState([]);
    const [isExtraFiltersOpen, setIsExtraFiltersOpen] = useState(false);

    useEffect(() => {
        let isMounted = true;

        getRemoteMenu()
            .then(({ content }) => {
                if (isMounted && content.length > 0) {
                    setCurrentMenu(content);
                }
            })
            .catch(() => {
                // Il menu locale resta disponibile se la rete non risponde.
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const categoryButtons = useMemo(
        () => [
            { id: "all", label: "Tutte" },
            ...currentMenu.map((category) => ({
                id: category.id,
                label:
                    category.title ||
                    categoryLabels[category.id] ||
                    category.sections[0]?.title ||
                    category.id,
            })),
        ],
        [currentMenu]
    );

    const toggleExtraFilter = (filterId) => {
        setActiveExtraFilters((prev) =>
            prev.includes(filterId)
                ? prev.filter((item) => item !== filterId)
                : [...prev, filterId]
        );
    };

    const filteredMenu = useMemo(() => {
        const categories =
            activeCategory === "all"
                ? currentMenu
                : currentMenu.filter((category) => category.id === activeCategory);

        return categories
            .map((category) => {
                const filteredSections = category.sections
                    .map((section) => {
                        const filteredItems =
                            activeExtraFilters.length === 0
                                ? section.items
                                : section.items.filter((item) => {
                                    const itemTags = item.tags || [];
                                    return activeExtraFilters.every((filter) =>
                                        itemTags.includes(filter)
                                    );
                                });

                        return {
                            ...section,
                            items: filteredItems,
                        };
                    })
                    .filter((section) => section.items.length > 0);

                return {
                    ...category,
                    sections: filteredSections,
                };
            })
            .filter((category) => category.sections.length > 0);
    }, [activeCategory, activeExtraFilters, currentMenu]);

    return (
        <>
            <Seo
                title="Menu pizzeria a Cecina | Busca Vida"
                description="Scopri il menu di Busca Vida a Cecina: pizze classiche e speciali, schiacciatini, antipasti, primi, secondi e piatti di mare."
                path="/menu"
            />
            <Navbar />

            <main className="menu-page">
                <section className="menu-intro container">
                    <h2 id="menu-page-title">Il nostro Menu</h2>
                    <p>
                        Antipasti, primi, secondi e pizze di ogni tipo.
                        Scopri tutte le nostre specialità.
                    </p>
                    <MenuPdfButton />
                </section>

                <section className="menu-filters-wrapper container">
                    <div className="menu-filters">
                        {categoryButtons.map((button) => (
                            <button
                                key={button.id}
                                type="button"
                                className={`menu-filter-button ${
                                    activeCategory === button.id ? "active" : ""
                                }`}
                                onClick={() => {
                                    setActiveCategory(button.id);
                                    setIsExtraFiltersOpen(false);
                                }}
                            >
                                {button.label}
                            </button>
                        ))}
                    </div>

                    <div className="menu-extra-filters-block">
                        <div className="menu-extra-filters-header">
                            <button
                                type="button"
                                className={`menu-extra-toggle ${
                                    isExtraFiltersOpen ? "open" : ""
                                }`}
                                onClick={() => setIsExtraFiltersOpen((prev) => !prev)}
                            >
                                <SlidersHorizontal size={18} />
                                <span>Filtri</span>

                                {activeExtraFilters.length > 0 && (
                                    <span className="menu-extra-count">
                                        {activeExtraFilters.length}
                                    </span>
                                )}
                            </button>

                            {activeExtraFilters.length > 0 && (
                                <button
                                    type="button"
                                    className="menu-reset-filters"
                                    onClick={() => setActiveExtraFilters([])}
                                >
                                    Reset
                                </button>
                            )}
                        </div>

                        {isExtraFiltersOpen && (
                            <div className="menu-extra-panel">
                                <div className="menu-extra-filters">
                                    {extraFilterButtons.map((button) => (
                                        <button
                                            key={button.id}
                                            type="button"
                                            className={`menu-filter-pill ${
                                                activeExtraFilters.includes(button.id)
                                                    ? "active"
                                                    : ""
                                            }`}
                                            onClick={() => toggleExtraFilter(button.id)}
                                        >
                                            {button.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {filteredMenu.length > 0 ? (
                    filteredMenu.map((category) => (
                        <section
                            key={category.id}
                            className="container menu-main-section"
                        >
                            <h2 className="menu-main-title">{category.title}</h2>

                            {category.sections.map((section) => (
                                <MenuCategory
                                    key={section.id}
                                    title={section.title}
                                    items={section.items}
                                />
                            ))}
                        </section>
                    ))
                ) : (
                    <section className="container menu-empty-state">
                        <p>Nessun elemento trovato con questi filtri.</p>
                    </section>
                )}
            </main>

            <Footer />
        </>
    );
}
